import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';

/**
 * ProfileCompletenessMeter — visualizes how much of the user's profile is
 * filled in and surfaces direct entry points to anything still empty.
 *
 * Rendered on the Profile page. Hidden once the profile is 100% complete —
 * no point celebrating a checkbox forever.
 *
 * Each "track" maps to a section users can fill via either:
 *   - The relevant Profile section's edit pencil, or
 *   - The contextual first-use prompt that appears when they tap into the
 *     feature for the first time (e.g. Equipment button on Strength)
 */

const TRACKS = [
  {
    id: 'basics',
    label: 'Mode + experience',
    desc: 'Athlete or Adaptive, and your training experience',
    isComplete: (p) => !!p.experience && (p.adaptiveMode !== undefined),
    href: '/onboarding/full#mode',
  },
  {
    id: 'goals',
    label: 'Goals',
    desc: 'What you want out of training',
    isComplete: (p) => Array.isArray(p.goals) && p.goals.length > 0,
    href: '/onboarding/full#experience_goals',
  },
  {
    id: 'equipment',
    label: 'Equipment',
    desc: 'What you can train with',
    isComplete: (p) => p.fullGym || p.noEquipment || (Array.isArray(p.equipment) && p.equipment.length > 0),
    href: '/onboarding/full#equipment',
  },
  {
    id: 'commitment',
    label: 'Time + frequency',
    desc: 'Days per week, minutes per session',
    isComplete: (p) => !!p.daysPerWeek && !!p.minutesPerSession,
    href: '/onboarding/full#training_structure',
  },
  {
    id: 'injuries',
    label: 'Injuries & limitations',
    desc: 'So the coach trains around them',
    isComplete: (p) => p.noInjuries === true ||
      (Array.isArray(p.injuryRegions) && p.injuryRegions.length > 0) ||
      (Array.isArray(p.injurySystemic) && p.injurySystemic.length > 0) ||
      (p.injuries && typeof p.injuries === 'object' && Object.values(p.injuries).some(Boolean)),
    href: '/onboarding/full#injuries',
  },
  {
    id: 'recovery',
    label: 'Endurance activities',
    desc: 'Walking, cycling, climbing, swimming, etc.',
    isComplete: (p) => Array.isArray(p.recoveryActivities) && p.recoveryActivities.length > 0,
    href: '/onboarding/full#recovery',
  },
];

const ProfileCompletenessMeter = () => {
  const { profile } = useProfile();
  const navigate = useNavigate();

  const { completed, incomplete, percent } = useMemo(() => {
    const done = [];
    const remaining = [];
    TRACKS.forEach((t) => {
      (t.isComplete(profile) ? done : remaining).push(t);
    });
    return {
      completed: done,
      incomplete: remaining,
      percent: Math.round((done.length / TRACKS.length) * 100),
    };
  }, [profile]);

  if (incomplete.length === 0) return null; // Don't render once everything's filled

  return (
    <div className="mt-8 bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5" data-testid="profile-completeness-meter">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Profile completeness</div>
          <div className="text-2xl font-display tracking-wide">{percent}%</div>
        </div>
        <div className="text-xs text-enf-text-secondary">
          {completed.length}/{TRACKS.length} sections
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-enf-surface-mid rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-red-700 to-red-500 transition-all duration-700 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Incomplete tracks — clickable */}
      <div className="space-y-1.5">
        {incomplete.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => navigate(t.href)}
            className="w-full flex items-center justify-between text-left p-2.5 -mx-2.5 rounded-md hover:bg-enf-surface-mid/70 transition-colors group"
            data-testid={`profile-track-${t.id}`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-1.5 h-1.5 rounded-full bg-enf-surface-elevated group-hover:bg-enf-accent flex-shrink-0 transition-colors" />
              <div className="min-w-0">
                <div className="text-sm text-enf-text-primary truncate">{t.label}</div>
                <div className="text-xs text-enf-text-muted truncate">{t.desc}</div>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-enf-text-muted group-hover:text-enf-text-secondary flex-shrink-0 transition-colors" />
          </button>
        ))}
      </div>

      {/* Already-done collapsed indicator (cheap visual reward without bloat) */}
      {completed.length > 0 && (
        <div className="mt-3 pt-3 border-t border-enf-surface-border text-xs text-enf-text-muted flex items-center gap-2">
          <Check className="w-3 h-3 text-emerald-500" />
          {completed.length} {completed.length === 1 ? 'section' : 'sections'} complete: {completed.map((c) => c.label).join(' · ')}
        </div>
      )}
    </div>
  );
};

export default ProfileCompletenessMeter;
